import { Injectable, OnDestroy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ApolloQueryResult } from '@apollo/client';
import { interval, Observable, of as observableOf, ReplaySubject, Subject, zip, combineLatest } from 'rxjs';
import { concatMap, debounce, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { ChannelRoutingGraphQL } from '@app/appointment/channel-routing-graphql.service';
import { ApiService } from '@app/core/api.service';
import { AppointmentAnalyticsBaseService } from '@app/core/appointment-analytics-base.service';
import { FeatureFlags } from '@app/core/feature-flags/feature-flags';
import { LaunchDarklyService } from '@app/core/feature-flags/launchdarkly.service';
import { MarkdownService } from '@app/core/markdown.service';
import { UserService } from '@app/core/user.service';
import { ContentBlock, ContentBlockVariables } from '@app/home/__generated__/content-block-graphql.service.types';
import { ContentBlockGraphQL } from '@app/home/content-block-graphql.service';
import { ContentBlockService } from '@app/shared';
import { Office } from '@app/shared/office';
import { Provider } from '@app/shared/provider';
import { State, StateService } from '@app/shared/state.service';

import { AppointmentAnalyticsService } from '../appointment-analytics.service';
import { AppointmentType } from '../appointment-type';
import { AppointmentService } from '../appointment.service';
import { FromAppointment } from '../from-appointment';
import { AppointmentInventory } from '../provider-inventories';
import { StructuredReasonForVisit } from '../structured-reason-for-visit';
import { AppointmentBookingState, AppointmentBookingType } from './appointment-booking-state';

interface AppointmentForRescheduling {
  id: string;
  startAt: string;
  timezoneTzinfo: string;
  licensingBodyShortName?: string | null;
  office: {
    name: string;
  };
  provider: {
    displayName: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AppointmentBookingStateService implements OnDestroy {
  contentForSelectAppointmentType$: ReplaySubject<string | null>;
  states$: Observable<State[]>;

  private appointmentBookingState: AppointmentBookingState;
  private destroy$ = new Subject<void>();

  constructor(
    private apiService: ApiService,
    private appointmentAnalyticsService: AppointmentAnalyticsService,
    private appointmentService: AppointmentService,
    private channelRoutingGraphQL: ChannelRoutingGraphQL,
    private launchDarklyService: LaunchDarklyService,
    private markdownService: MarkdownService,
    private router: Router,
    private userService: UserService,
    private contentBlockService: ContentBlockService,
    private contentBlockGraphQL: ContentBlockGraphQL,
    private stateService: StateService,
  ) {
    this.states$ = this.stateService.states$.pipe(
      map(states => states?.filter(state => state.isRpcEligible === true) || []),
    );
    this.resetAppointmentBookingState();
    this.updateBookingStateOnUserServiceAreaChange();
  }

  setAppointmentBookingState(appointmentBookingState: AppointmentBookingState) {
    this.appointmentBookingState = appointmentBookingState;

    this.userService.user$.pipe(take(1)).subscribe(user => {
      if (!this.appointmentBookingState.serviceAreaSet()) {
        this.appointmentBookingState.setSelectedServiceArea(user.serviceArea);
      }
    });

    this.setHasAccessToRemotePrimaryCare();
    this.setStateFromUserAddress();

    this.appointmentAnalyticsService.setBookingState(appointmentBookingState);
    this.initContentForSelectAppointmentType();
  }

  private setHasAccessToRemotePrimaryCare(): void {
    this.userService
      .hasAccessToRpc$()
      .pipe(take(1))
      .subscribe(hasAccess => {
        this.appointmentBookingState.setHasAccessToRemotePrimaryCare(hasAccess);
      });
  }

  private setStateFromUserAddress(): void {
    if (this.appointmentBookingState.getSelectedState()) {
      return;
    }

    this.userService
      .hasAccessToRpc$()
      .pipe(
        take(1),
        switchMap(hasAccess => {
          if (!hasAccess) {
            return observableOf(void 0);
          }

          return combineLatest([this.userService.getUser(), this.states$]).pipe(
            take(1),
            tap(([user, states]) => {
              if (user?.address?.state && states) {
                const matchingState = states.find(state => state.shortName === user.address.state);
                if (matchingState) {
                  this.appointmentBookingState.setSelectedState(matchingState);
                }
              }
            }),
          );
        }),
      )
      .subscribe();
  }

  getAppointmentBookingState() {
    return this.appointmentBookingState;
  }

  resetAppointmentBookingState() {
    this.setAppointmentBookingState(new AppointmentBookingState());
  }

  setIsRecommendedRemoteAppointment(isRecommendedRemoteAppointment: boolean) {
    this.appointmentBookingState.setIsRecommendedRemoteAppointment(isRecommendedRemoteAppointment);
  }

  initContentForSelectAppointmentType() {
    const content$ = this.appointmentBookingState.selectedServiceArea$.pipe(
      debounce(() => interval(200)),
      concatMap(serviceArea => zip(observableOf(serviceArea))),
      concatMap(([serviceArea]) => {
        const serviceAreaId = serviceArea ? serviceArea.id : null;
        return this.getContentBlockAsString(
          'appointment-type',
          'default',
          'appointment-type-select-header-web',
          serviceAreaId,
        );
      }),
    );
    const contentSubject$: ReplaySubject<string | null> = new ReplaySubject(1);
    content$.subscribe(contentSubject$);
    this.contentForSelectAppointmentType$ = contentSubject$;
  }

  contentForSearchAppointmentType(): Observable<SafeHtml> {
    return this.appointmentBookingState.selectedServiceArea$.pipe(
      take(1),
      switchMap(serviceArea => {
        const serviceAreaId = serviceArea ? serviceArea.id : null;
        const appointmentTypeId = this.appointmentBookingState.appointmentType?.id;

        return this.getContentBlock(
          'appointment-type',
          appointmentTypeId,
          'appointment-type-search-content',
          serviceAreaId,
        );
      }),
    );
  }

  setStateForRescheduling(appointment: AppointmentForRescheduling) {
    this.appointmentBookingState.bookingType = AppointmentBookingType.Reschedule;
    const fromAppointment = new FromAppointment();
    fromAppointment.id = parseInt(appointment.id, 10);
    fromAppointment.officeName = appointment.office.name;
    fromAppointment.providerName = appointment.provider.displayName;
    fromAppointment.startTime = appointment.startAt;
    fromAppointment.displayTimezone = appointment.timezoneTzinfo;
    this.appointmentBookingState.setFromAppointment(fromAppointment);

    if (appointment.licensingBodyShortName) {
      this.states$.pipe(take(1)).subscribe(states => {
        const matchingState = states.find(state => state.shortName === appointment.licensingBodyShortName);
        if (matchingState) {
          this.appointmentBookingState.setSelectedState(matchingState);
        }
      });
    }
  }

  setStateForReviewBooking(
    selectedInventory: AppointmentInventory | undefined | null,
    inventoryProvider: Provider,
    office: Office,
    analyticsService: AppointmentAnalyticsBaseService,
    locationAttestation: string | undefined | null,
    afterHoursBillingDisclosure?: boolean,
    isAppointmentSeries = false,
  ) {
    this.appointmentBookingState.setAttributesForReviewBooking(
      selectedInventory,
      inventoryProvider,
      office,
      analyticsService,
      locationAttestation,
      afterHoursBillingDisclosure,
      isAppointmentSeries,
    );
  }

  updateAppointmentType(appointmentType: AppointmentType) {
    this.appointmentBookingState.setSelectedAppointmentType(appointmentType);
  }

  updateForSwitchToRemote(reason: string, flow: string, source: string) {
    this.appointmentBookingState.bookingType = AppointmentBookingType.SwitchToRemote;
    this.appointmentBookingState.reason = reason;
    this.appointmentBookingState.flow = flow;
    this.appointmentBookingState.source = source;
  }

  private updateBookingStateOnUserServiceAreaChange() {
    this.launchDarklyService
      .featureFlag$(FeatureFlags.OMNI_MSA_STOP_SERVICE_AREA_REFRESH, false)
      .pipe(take(1))
      .subscribe(featureFlagEnabled => {
        // Don't subscribe to updating serviceArea if FF is turned ON
        if (!featureFlagEnabled) {
          this.userService.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
            this.appointmentBookingState.setSelectedServiceArea(user.serviceArea);
          });
        }
      });
  }

  setStruturedReasonForVisitMetadata(metadata: StructuredReasonForVisit) {
    this.appointmentBookingState.setStructuredReasonForVisit(metadata);
  }

  setSelectedReasonForVisit(selectedRFV: {
    term: string;
    id: string;
    appointmentTypeIds: number[];
    structuredVisitReasonFallback: boolean;
  }) {
    this.appointmentBookingState.setSelectedReasonForVisit(selectedRFV);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getContentBlock(
    section: string,
    page: string | number,
    title: string,
    serviceAreaId: string | number | null,
  ): Observable<SafeHtml | null> {
    return this.launchDarklyService
      .featureFlag$(FeatureFlags.GRAPHQL_MIGRATION_BOOKING_STATE_CONTENT_BLOCK, false)
      .pipe(
        switchMap(useGraphQL => {
          if (useGraphQL) {
            return this.fetchContentBlock({
              section,
              page: page.toString(),
              title,
              serviceAreaId: serviceAreaId?.toString(),
            });
          } else {
            return this.contentBlockService
              .getContentBlock(section, page, title, serviceAreaId)
              .pipe(map(resp => (resp.html ? this.markdownService.convertWithHeadings(resp.html) : null)));
          }
        }),
        take(1),
      );
  }

  private getContentBlockAsString(
    section: string,
    page: string | number,
    title: string,
    serviceAreaId: string | number | null,
  ): Observable<string | null> {
    return this.getContentBlock(section, page, title, serviceAreaId).pipe(
      map(safeHtml => (safeHtml ? safeHtml.toString() : null)),
    );
  }

  private fetchContentBlock(contentBlockVariables: ContentBlockVariables): Observable<SafeHtml | null> {
    return this.contentBlockGraphQL
      .fetch(contentBlockVariables)
      .pipe(
        map((result: ApolloQueryResult<ContentBlock>) =>
          result.data.contentBlock?.copy
            ? this.markdownService.convertWithHeadings(result.data.contentBlock.copy)
            : null,
        ),
      );
  }
}
